import { Box, ColorOptions, Icon, Spacer, Text } from '@jfs/ui-new'
import { get } from 'lodash'
import { Fragment, useCallback, useEffect, useState } from 'react'
import { FlatList } from 'react-native'
import { menuCategories } from '../../data'
import { useStores } from '../../data/store'
import { getMenuItems } from '../../screens/dashboard'

interface SectionListProps {
  data: any
  onItemPress?: (data: any) => void
  onSectionPress?: (data: any) => void
  userActionDisabled?: boolean
}

const getEssApprovalsAccess = (obj) => {
  const crdrApproval = get(obj, ['crdr_adjustment', 'approve', 'isAccessible'], false)
  const riskControlTxnApproval = get(obj, ['risk_config', 'approve', 'isAccessible'], false)
  const riskControlTxnMidApproval = get(obj, ['risk_config', 'approve', 'isAccessible'], false)
  const riskControlPayoutCmsApproval = get(obj, ['payout_cms_update', 'approve', 'isAccessible'], false)
  return crdrApproval || riskControlTxnApproval || riskControlTxnMidApproval || riskControlPayoutCmsApproval
}

const getEntConfigApprovalsAccess = (obj) => {
  const entMasterApproval = get(obj, ['entity_master', 'approve', 'isAccessible'], false)
  const hasEntityConfigApprovals = get(obj, ['processing_entity_config', 'approve', 'isAccessible'], false)
  return entMasterApproval || hasEntityConfigApprovals
}

const getRoutingApprovalAccess = (obj) => {
  const pkgApproval = get(obj, ['package', 'approve', 'isAccessible'], false)
  const pkgFileApproval = get(obj, ['package_file_upload', 'approve', 'isAccessible'], false)
  const midPkgApproval = get(obj, ['mid_package_mapping', 'approve', 'isAccessible'], false)
  return pkgApproval || pkgFileApproval || midPkgApproval
}

const ExpandList = ({ data, userActionDisabled = false, onSectionPress, onItemPress }: SectionListProps) => {
  const { accessConfig, resetDataEntered, forceSideMenuRefresh, setSideMenuRefresh } = useStores((store) => ({
    accessConfig: store.user.accessConfig,
    resetDataEntered: store.onboarding.resetDataEntered,
    forceSideMenuRefresh: store.forceSideMenuRefresh,
    setSideMenuRefresh: store.setSideMenuRefresh,
  }))

  const [sectionData, setSectionData] = useState(data)
  const [hover, setHoverColor] = useState<{ isHovering: boolean; value: string }>({ isHovering: false, value: '' })
  const [titleHovering, setIsTitleHovered] = useState<{ isHovering: boolean; value: string }>({
    isHovering: false,
    value: '',
  })
  // const [selectedSection, setSelectedSection] = useState('')
  const [selectedItem, setSelectedItem] = useState<any>()
  const [expandedCats, setExpandedCats] = useState<{ [key: string]: boolean }>({})
  const [catHover, setCatHover] = useState('')
  const [expandedSubSections, setExpandedSubSections] = useState<{ [key: string]: boolean }>({})

  const toggleSubSection = (title: string) => {
    setExpandedSubSections((prev) => ({ ...prev, [title]: !prev[title] }))
  }

  // Auto-expand the category that owns the currently active section so the selected tab is visible.
  const getActiveCats = (sections: any[]) => {
    const map: { [key: string]: boolean } = {}
    const activeSection = sections?.find((s: any) => s?.isExpand)
    if (activeSection) {
      const cat = menuCategories.find((c: any) =>
        c.sections.some((entry: any) => {
          const title = typeof entry === 'string' ? entry : entry.title
          const subs = typeof entry === 'string' ? [] : entry.subSections ?? []
          return title === activeSection.title || subs.includes(activeSection.title)
        }),
      )
      if (cat) map[cat.name] = true
    }
    return map
  }

  const toggleCategory = (name: string) => {
    setExpandedCats((prev) => ({ ...prev, [name]: !prev[name] }))
  }

  type SectionType = {
    [key: string]: string[]
  }

  useEffect(() => {
    if (!window.location.pathname.includes('/searchEntity')) {
      const menu = getMenuItems()
      setSectionData(menu)
      setExpandedCats((prev) => ({ ...prev, ...getActiveCats(menu) }))
      setSideMenuRefresh(false)
    }
  }, [window.location.pathname, forceSideMenuRefresh, accessConfig])

  const hasSectionAccess = (title: string) => {
    const getAccess: SectionType = {
      ['Entity Config']: ['entity_config', 'isAccessible'],
      ['Merchant - Processor Configuration']: ['merchant_config', 'isAccessible'],
      ['Device Management']: ['merchant_config', 'isAccessible'],
      ['Smart Routing']: ['routing', 'isAccessible'],
      ['System Configuration']: ['sys_control_config', 'isAccessible'],
      ['Access Control']: ['access_management', 'isAccessible'],
      // [`Merchant Onboarding`]: ['access_management', 'isAccessible'], //TODO: change key
      ['Management']: ['entity_management_service', 'isAccessible'],
      ['Onboarding']: ['entity_management_service', 'isAccessible'],
      ['Entity Onboarding']: ['entity_management_service', 'isAccessible'],
      ['Entity Transactions']: ['ess', 'isAccessible'],
      ['Application Queue']: ['queue', 'checker_queue', 'global_queue', 'isAccessible'],
      ['My Queue (Checker)']: ['queue', 'checker_queue', 'my_queue', 'isAccessible'],
      ['Global Queue']: ['queue', 'checker_queue', 'global_queue', 'isAccessible'],
      ['Queue - Maker']: ['queue', 'maker_queue', 'isAccessible'],
      ['Queue - Checker']: ['queue', 'checker_queue', 'isAccessible'],
      ['Bulk Processing']: ['bulk_processing', 'isAccessible'],
      ['FIU Compliance']: ['fiu_compliance', 'isAccessible'],
      ['Fee Package']: ['mdr', 'isAccessible'],
      ['Payout Configurations']: ['payout_config', 'isAccessible'],
      ['System Settings']: ['ess', 'isAccessible'],
      ['Adjustments']: ['ess', 'isAccessible'],
      ['Update Records']: ['ess', 'isAccessible'],
      ['Aadhaar Redaction']: ['aadhar_redaction', 'search', 'isAccessible'],
      ['Post Recon Payout']: ['post_recon', 'fetch', 'isAccessible'],
      ['POS']: ['pos', 'isAccessible'],
      ['POS Onboarding']: ['pos', 'isAccessible'],
    }
    try {
      const accessJson = JSON.parse(accessConfig)
      if (title === 'Management') {
        return (
          get(accessJson, ['entity_management_service', 'isAccessible'], false) ||
          get(accessJson, ['queue', 'checker_queue', 'isAccessible'], false) ||
          get(accessJson, ['queue', 'maker_queue', 'isAccessible'], false)
        )
      }
      const access = get(accessJson, getAccess[title], false)
      return access
    } catch (error) {
      // console.log('hasSectionAccess', { error })
    }
  }

  // True when the user can open at least one tab inside the section (or it is a direct link with no tabs).
  // Used so a parent whose tabs are all hidden for this role is not shown as an empty header.
  const hasVisibleItems = (section: any) =>
    !section?.data?.length || section.data.some((subItem: any) => hasItemAccess(subItem.name, accessConfig))

  const isSectionVisible = (title: string) => {
    const section = sectionData?.find((s: any) => s.title === title)
    return !!section && !!hasSectionAccess(title) && hasVisibleItems(section)
  }

  const updateSectionData = useCallback(
    (section: any, secData: any) => {
      const updatedData = secData.map((item: any) => {
        if (item.title === section.title) {
          return {
            ...item,
            isExpand: !item.isExpand,
          }
        } else {
          let isExpanded = false
          item.data.map((subItem: any) => {
            if (section.name === subItem.name) {
              isExpanded = true
            }
          })
          return {
            ...item,
            isExpand: isExpanded,
          }
        }
      })
      setSectionData(updatedData)
    },
    [setSectionData],
  )

  const renderItemView = (item: any, index: any) => {
    if ((item && !item.isExpand) || item?.data?.length === 0) {
      return null
    }

    const renderItem = ({ item }: any) => {
      if (!hasItemAccess(item.name, accessConfig)) {
        return null
      }

      const handleHover = (isHovering: boolean) => {
        setHoverColor({ isHovering: isHovering, value: isHovering ? item.name : '' })
      }

      const paths: any = {
        merchantDetails: 'merchantmapping',
        editEntity: 'entitymaster',
        editBankingCode: 'bankingcodes',
        userDetails: 'users',
        addusers: 'users',
        ledgerDetails: 'ledgermapping',
        holdtransaction: 'riskconfig',
        processingentitydetails: 'processingentity',
        'fAndRAction/riskHold': 'fAndRAction',
        'fAndRAudit/riskHoldAudit': 'riskHoldAudit',
        'fAndRAudit/riskHoldableAudit': 'riskHoldableAudit',
      }
      const getPath = () => {
        return paths[window.location.pathname.slice(1)] ?? window.location.pathname.slice(1)
      }
      const isItemSelected = selectedItem?.name === item?.name || item.name === getPath()

      const getBackgroundColor = () => {
        if (isItemSelected || (hover.isHovering && hover.value === item.name)) {
          return 'WHITE_SEPERATOR' as ColorOptions
        }
        return 'TRANSPARENT'
      }

      const onItemClick = (item1: any) => {
        localStorage.setItem('sortingOrder', '')
        setSelectedItem(item1)
        onItemPress && onItemPress(item1)
        updateSectionData(item, sectionData)
        resetDataEntered()
      }

      return (
        <Box
          key={index}
          flexDirection={'row'}
          alignItems={'stretch'}
          backgroundColor={getBackgroundColor()}
          onHover={handleHover}
          testID={`menu-subtitle-${item.title}`}
          onPress={() => onItemClick(item)}
          underlayColor={'TRANSPARENT'}>
          <Box w={4} backgroundColor={isItemSelected ? 'WHITE' : ('TRANSPARENT' as ColorOptions)} />
          <Box f={1} paddingLeft={48} paddingRight={8} paddingVertical={9}>
            <Text
              color={'WHITE'}
              fontFamily={'jiotype-medium'}
              fontSize={14}
              opacity={isItemSelected ? 1 : 0.75}
              title={item.title}
              lineHeight={20}
            />
          </Box>
        </Box>
      )
    }
    return (
      <Box>
        <FlatList data={item.data} renderItem={renderItem} />
      </Box>
    )
  }
  const renderSectionHeader = ({ item, index }: any) => {
    if (!hasSectionAccess(item.title) || !hasVisibleItems(item)) {
      return null
    }
    const handleHoverOnTitle = (isHovering: boolean) => {
      setIsTitleHovered({ isHovering: isHovering, value: isHovering ? item.title : '' })
    }

    const onSectionClick = (section: any) => {
      updateSectionData(section, sectionData)
      onSectionPress && onSectionPress(section)
    }

    const getArrowIcon = (isExpand: boolean) => (isExpand ? 'ARROW_DOWN' : 'ARROW_RIGHT')
    return (
      <>
        <Box
          disabled={userActionDisabled}
          testID={`menu-title-${item.title}`}
          onPress={() => onSectionClick(item)}
          underlayColor={'TRANSPARENT'}
          paddingLeft={40}
          paddingRight={16}
          paddingVertical={12}
          flexDirection={'row'}
          onHover={handleHoverOnTitle}
          justifyContent={'space-between'}>
          <Box flexDirection={'row'} align={'center'} f={1}>
            <Text
              color={'WHITE'}
              fontSize={14}
              title={item.title}
              fontFamily={'jiotype-medium'}
              opacity={titleHovering.isHovering && titleHovering.value === item.title ? 1 : 0.85}
            />
          </Box>
          {item.data.length > 0 && (
            <Icon id={getArrowIcon(item.isExpand)} color={'WHITE50'} size={'FONT_SIZE_DOUBLE_LARGE'} />
          )}
        </Box>
        {renderItemView(item, index)}
      </>
    )
  }
  // A leaf tab shown directly under a category (no intermediate section header).
  const renderDirectItem = (rawItem: any, index: number) => {
    const item = { name: rawItem.path.split('/')[1], title: rawItem.name }
    if (!hasItemAccess(item.name, accessConfig)) {
      return null
    }
    const paths: any = {
      merchantDetails: 'merchantmapping',
      editEntity: 'entitymaster',
      editBankingCode: 'bankingcodes',
      userDetails: 'users',
      addusers: 'users',
      ledgerDetails: 'ledgermapping',
      holdtransaction: 'riskconfig',
      processingentitydetails: 'processingentity',
    }
    const getPath = () => paths[window.location.pathname.slice(1)] ?? window.location.pathname.slice(1)
    const isSelected = selectedItem?.name === item.name || item.name === getPath()
    const onClick = () => {
      localStorage.setItem('sortingOrder', '')
      setSelectedItem(item)
      onItemPress && onItemPress(item)
      resetDataEntered()
    }
    return (
      <Box
        key={`direct-${index}`}
        flexDirection={'row'}
        alignItems={'stretch'}
        backgroundColor={isSelected ? ('WHITE_SEPERATOR' as ColorOptions) : 'TRANSPARENT'}
        onPress={onClick}
        underlayColor={'TRANSPARENT'}
        testID={`menu-direct-${item.title}`}>
        <Box w={4} backgroundColor={isSelected ? 'WHITE' : ('TRANSPARENT' as ColorOptions)} />
        <Box f={1} paddingLeft={40} paddingRight={16} paddingVertical={12}>
          <Text
            color={'WHITE'}
            fontFamily={'jiotype-medium'}
            fontSize={14}
            opacity={isSelected ? 1 : 0.85}
            title={item.title}
          />
        </Box>
      </Box>
    )
  }
  // A leaf tab shown inside a nested sub-section (deeper indent).
  const renderSubLeaf = (leaf: any, index: number) => {
    if (!hasItemAccess(leaf.name, accessConfig)) {
      return null
    }
    const isSelected = selectedItem?.name === leaf.name || leaf.name === window.location.pathname.slice(1)
    const onClick = () => {
      localStorage.setItem('sortingOrder', '')
      setSelectedItem(leaf)
      onItemPress && onItemPress(leaf)
      resetDataEntered()
    }
    return (
      <Box
        key={`subleaf-${index}`}
        flexDirection={'row'}
        alignItems={'stretch'}
        backgroundColor={isSelected ? ('WHITE_SEPERATOR' as ColorOptions) : 'TRANSPARENT'}
        onPress={onClick}
        underlayColor={'TRANSPARENT'}
        testID={`menu-subleaf-${leaf.title}`}>
        <Box w={4} backgroundColor={isSelected ? 'WHITE' : ('TRANSPARENT' as ColorOptions)} />
        <Box f={1} paddingLeft={60} paddingRight={8} paddingVertical={9}>
          <Text
            color={'WHITE'}
            fontFamily={'jiotype-medium'}
            fontSize={14}
            opacity={isSelected ? 1 : 0.75}
            title={leaf.title}
          />
        </Box>
      </Box>
    )
  }

  // An expandable sub-group rendered nested inside a section (4th level).
  const renderSubSection = (subSec: any) => {
    const hasActiveLeaf = subSec?.data?.some((leaf: any) => leaf.name === window.location.pathname.slice(1))
    const isSubExpanded = !!expandedSubSections[subSec.title] || hasActiveLeaf
    return (
      <Fragment key={`subsec-${subSec.title}`}>
        <Box
          testID={`menu-subsection-${subSec.title}`}
          onPress={() => toggleSubSection(subSec.title)}
          underlayColor={'TRANSPARENT'}
          paddingLeft={44}
          paddingRight={16}
          paddingVertical={11}
          flexDirection={'row'}
          justifyContent={'space-between'}>
          <Text color={'WHITE'} fontSize={13.6} title={subSec.title} fontFamily={'jiotype-medium'} opacity={0.85} />
          {subSec?.data?.length > 0 && (
            <Icon id={isSubExpanded ? 'ARROW_DOWN' : 'ARROW_RIGHT'} color={'WHITE50'} size={'FONT_SIZE_DOUBLE_LARGE'} />
          )}
        </Box>
        {isSubExpanded && subSec.data.map((leaf: any, i: number) => renderSubLeaf(leaf, i))}
      </Fragment>
    )
  }

  const renderCategory = ({ item: category }: any) => {
    // Section entries: a string title, or { title, subSections: [...] } for nesting.
    const sectionEntries = category.sections
      .map((entry: any) => {
        const title = typeof entry === 'string' ? entry : entry.title
        const subSectionTitles = typeof entry === 'string' ? [] : entry.subSections ?? []
        return { section: sectionData?.find((s: any) => s.title === title), subSectionTitles }
      })
      .filter(
        (e: any) =>
          e.section &&
          (isSectionVisible(e.section.title) || e.subSectionTitles.some((t: string) => isSectionVisible(t))),
      )

    // Direct leaf tabs shown under the category with no intermediate section header.
    const directItems = (category.items ?? []).filter((it: any) => hasItemAccess(it.path.split('/')[1], accessConfig))

    if (sectionEntries.length === 0 && directItems.length === 0) {
      return null
    }

    const isExpanded = !!expandedCats[category.name]
    const isHovered = catHover === category.name

    return (
      <>
        <Box
          testID={`menu-category-${category.name}`}
          onPress={() => toggleCategory(category.name)}
          underlayColor={'TRANSPARENT'}
          paddingHorizontal={16}
          paddingVertical={16}
          flexDirection={'row'}
          onHover={(isHovering: boolean) => setCatHover(isHovering ? category.name : '')}
          justifyContent={'space-between'}>
          <Box flexDirection={'row'} align={'center'} f={1}>
            <Icon id={category.icon} color={'WHITE'} size={16} />
            <Spacer direction={'horizontal'} size={8} />
            <Text
              color={'WHITE'}
              fontSize={14}
              title={category.name}
              fontFamily={'jiotype-bold'}
              opacity={isHovered || isExpanded ? 1 : 0.9}
            />
            {!!category.example && (
              <Box
                marginLeft={6}
                paddingHorizontal={5}
                paddingVertical={1}
                borderRadius={3}
                borderWidth={1}
                borderColor={'WHITE50'}>
                <Text color={'WHITE'} fontSize={9} title={category.example} opacity={0.7} />
              </Box>
            )}
          </Box>
          <Icon id={isExpanded ? 'ARROW_DOWN' : 'ARROW_RIGHT'} color={'WHITE50'} size={'FONT_SIZE_DOUBLE_LARGE'} />
        </Box>
        {isExpanded && (
          <>
            {category.flat
              ? sectionEntries.map(({ section }: any) => (
                  <Fragment key={section.title}>{renderItemView({ ...section, isExpand: true }, 0)}</Fragment>
                ))
              : sectionEntries.map(({ section, subSectionTitles }: any, index: number) => {
                  const hasActiveSubLeaf = subSectionTitles.some((t: string) => {
                    const sub = sectionData?.find((s: any) => s.title === t)
                    return sub?.data?.some((leaf: any) => leaf.name === window.location.pathname.slice(1))
                  })
                  return (
                    <Fragment key={section.title}>
                      {renderSectionHeader({ item: section, index })}
                      {(section.isExpand || hasActiveSubLeaf) &&
                        subSectionTitles
                          .map((t: string) => sectionData?.find((s: any) => s.title === t))
                          .filter((s: any) => s && isSectionVisible(s.title))
                          .map((subSec: any) => renderSubSection(subSec))}
                    </Fragment>
                  )
                })}
            {directItems.map((it: any, index: number) => renderDirectItem(it, index))}
          </>
        )}
      </>
    )
  }

  return (
    <FlatList
      data={menuCategories}
      renderItem={renderCategory}
      extraData={{ sectionData, expandedCats, catHover, selectedItem, hover, titleHovering, accessConfig }}
      keyExtractor={(_, index) => '' + index}
      style={{ width: '100%', height: '100%', overflow: 'auto', paddingBottom: 20 }}
      nativeID="hv-scroll"
      persistentScrollbar
      scrollEnabled
      showsHorizontalScrollIndicator
      showsVerticalScrollIndicator
    />
  )
}

export { ExpandList }

export const hasItemAccess = (title: string, accessConfig: any) => {
  type TitleType = {
    [key: string]: { config: string; data: string[]; isApproval?: boolean; disabled?: boolean }
  }
  try {
    const accessJson: any = JSON.parse(accessConfig)
    const getAccess: TitleType = {
      ['entitymaster']: { config: accessJson.entity_config, data: ['entity_master', 'isAccessible'] },
      ['entityconfigurations']: {
        config: accessJson.entity_config,
        data: ['processing_entity_config', 'create', 'isAccessible'],
      },
      ['cardmapping']: { config: accessJson.entity_config, data: ['card_type_mapping', 'isAccessible'] },
      ['netbankingMapping']: { config: accessJson.entity_config, data: ['net_bank_mapping', 'isAccessible'] },
      ['merchantmapping']: { config: accessJson.merchant_config, data: ['merchant_mapping', 'isAccessible'] },
      ['saponboarding']: {
        config: accessJson.merchant_config,
        data: ['sap_onboarding', 'create', 'isAccessible'],
      },
      ['bulkmerchantmapping']: {
        config: accessJson.merchant_config,
        data: ['merchant_mapping', 'add', 'isAccessible'],
      },
      ['createbulkmerchantmapping']: {
        config: accessJson.merchant_config,
        data: ['merchant_mapping', 'add', 'isAccessible'],
      },
      ['createbulkkeyexchange']: {
        config: accessJson.merchant_config,
        data: ['merchant_mapping', 'add', 'isAccessible'],
      },
      ['approvebulkmerchantmapping']: {
        config: accessJson.merchant_config,
        data: ['merchant_mapping', 'approve', 'isAccessible'],
      },
      ['finHubReports']: {
        config: accessJson.merchant_config,
        data: ['finOps_Report', 'create', 'isAccessible'],
      },
      ['viewAndAddDevices']: {
        config: accessJson.merchant_config,
        data: ['device_variant', 'fetch', 'isAccessible'],
      },
      ['viewAndAddDevicePlans']: {
        config: accessJson.merchant_config,
        data: ['device_plans', 'fetch', 'isAccessible'],
      },
      ['onboardDevices']: {
        config: accessJson.merchant_config,
        data: ['finOps_Report', 'create', 'isAccessible'],
      },
      ['viewAssignedDevices']: {
        config: accessJson.merchant_config,
        data: ['finOps_Report', 'create', 'isAccessible'],
      },
      ['createpackage']: { config: accessJson.routing, data: ['package', 'isAccessible'] },
      ['packageinstrument']: {
        config: accessJson.routing,
        data: ['package_instrument_mapping', 'isAccessible'],
      },
      ['packagemid']: { config: accessJson.routing, data: ['mid_package_mapping', 'isAccessible'] },
      devices: { config: accessJson.merchant_config, data: ['device_mapping', 'isAccessible'] },
      emsdevices: { config: accessJson.merchant_config, data: ['device_mapping', 'isAccessible'] },
      users: { config: accessJson.access_management, data: ['user', 'fetch', 'isAccessible'] },
      // addusers: {
      //   config: accessJson.access_management,
      //   data: ['user', 'update_user', 'isAccessible'],
      // },
      usersgroup: {
        config: accessJson.access_management,
        data: ['data', 'group', 'data', 'fetch', 'isAccessible'],
      },
      addgroup: {
        config: accessJson.access_management,
        data: ['data', 'group', 'data', 'update', 'isAccessible'],
      }, // TODO:

      ['approvals']: {
        config: accessJson.entity_config,
        data: getEntConfigApprovalsAccess(accessJson.entity_config),
      },
      merchantMappingApprovals: {
        config: accessJson.merchant_config,
        data: ['merchant_mapping', 'approve', 'isAccessible'],
      },
      ['merchantapprovals']: {
        config: accessJson.merchant_config,
        data: ['merchant_mapping', 'approve', 'isAccessible'],
      },
      ['routingapprovals']: {
        config: accessJson.routing,
        data: getRoutingApprovalAccess(accessJson.routing),
      },
      ['dynamicrouting']: {
        config: accessJson.routing,
        data: ['dynamic_routing', 'view', 'isAccessible'],
      },
      fileupload: {
        config: accessJson.routing,
        data: ['package_file_upload', 'isAccessible'],
      },
      generateExcelFile: {
        config: accessJson.routing,
        data: ['data', 'rule_package_mapping', 'isAccessible'],
      },
      generateExcelFileApproval: {
        config: accessJson.routing,
        data: ['data', 'rule_package_mapping', 'data', 'approve', 'isAccessible'],
      },
      bankingcodes: { config: accessJson.sys_control_config, data: ['banking_codes', 'isAccessible'] },
      systemconfigapprovals: {
        config: accessJson.sys_control_config,
        data: ['banking_codes', 'approve', 'isAccessible'],
      },
      merchantbulkonboarding: {
        config: accessJson.merchant_config,
        data: ['bulk_merchant_onboarding', 'isAccessible'],
      },
      searchEntityHierarchy: {
        config: accessJson,
        data: ['entity_management_service'],
      },
      createEntityGroup: {
        config: accessJson.entity_management_service,
        data: ['group', 'create', 'isAccessible'],
      },
      createEntitySubGroup: {
        config: accessJson.entity_management_service,
        data: ['sub_group', 'read', 'isAccessible'],
      },
      createSingleEntity: {
        config: accessJson.entity_management_service,
        data: ['single_entity', 'read', 'isAccessible'],
      },
      createBulkEntities: {
        config: accessJson.bulk_processing,
        data: ['create_entities', 'isAccessible'],
      },
      createTID: {
        config: accessJson.bulk_processing,
        data: ['sync_entities_mas', 'isAccessible'],
      },
      merchantonboarding: {
        config: accessJson.sys_control_config,
        data: ['banking_codes', 'approve', 'isAccessible'],
      },
      entityManagement: {
        config: accessJson.entity_management_service,
        data: ['isAccessible'],
      },
      onboardEntity: {
        config: accessJson.entity_management_service,
        data: ['ems_config', 'create', 'isAccessible'],
      },
      makerMyEntityQueue: {
        config: accessJson.entity_management_service,
        data: ['ems_config', 'create', 'isAccessible'],
      },
      checker1MyQueue: {
        config: accessJson.entity_management_service,
        data: ['checkerOne', 'approve', 'isAccessible'],
      },
      checker2MyQueue: {
        config: accessJson.entity_management_service,
        data: ['checkerTwo', 'approve', 'isAccessible'],
      },
      riskCheckerMyQueue: {
        config: accessJson.entity_management_service,
        data: ['risk', 'approve', 'isAccessible'],
      },
      riskMakerMyQueue: {
        config: accessJson.entity_management_service,
        data: ['risk', 'create', 'isAccessible'],
      },
      checker1Queue: {
        config: accessJson.entity_management_service,
        data: ['checkerOne', 'approve', 'isAccessible'],
      },
      checker2Queue: {
        config: accessJson.entity_management_service,
        data: ['checkerTwo', 'approve', 'isAccessible'],
      },
      riskCheckerQueue: {
        config: accessJson.entity_management_service,
        data: ['risk', 'approve', 'isAccessible'],
      },
      riskMakerQueue: {
        config: accessJson.entity_management_service,
        data: ['risk', 'create', 'isAccessible'],
      },
      onboardingIncidents: {
        config: accessJson.entity_management_service,
        data: ['isAccessible'],
      },
      ['createfeepackage']: { config: accessJson.mdr, data: ['package', 'isAccessible'] },
      ['feepackagefileupload']: {
        config: accessJson.mdr,
        data: ['package_file_upload', 'isAccessible'],
      },
      ['feepackagemid']: { config: accessJson.mdr, data: ['mid_package_mapping', 'isAccessible'] },
      ['feepackageapprovals']: {
        config: accessJson.mdr,
        data: getRoutingApprovalAccess(accessJson.mdr),
      },
      ['merchantconfig']: {
        config: accessJson.payout_config,
        data: ['isAccessible'],
      },
      ['bulkmerchantpayoutconfig']: {
        config: accessJson.payout_config,
        data: ['isAccessible'],
      },
      ['onholdsettlements']: {
        config: accessJson.payout_config,
        data: ['fetch', 'isAccessible'],
      },
      ['merchantpayoutconfigapprovals']: {
        config: accessJson.payout_config,
        data: ['approve', 'isAccessible'],
      },
      ['bulkmerchantpayoutconfigapprovals']: {
        config: accessJson.payout_config,
        data: ['approve', 'isAccessible'],
      },
      checkerMyQueue: {
        config: accessJson.queue,
        data: ['checker_queue', 'my_queue', 'isAccessible'],
      },
      checkerGlobalQueue: {
        config: accessJson.queue,
        data: ['checker_queue', 'global_queue', 'isAccessible'],
      },
      makerMyQueue: {
        config: accessJson.queue,
        data: ['maker_queue', 'my_queue', 'isAccessible'],
      },
      esscontrolupdate: {
        config: accessJson.ess,
        data: ['ess_control_update', 'isAccessible'],
      },
      payoutconfig: {
        config: accessJson.ess,
        data: ['payout_frequency_config', 'isAccessible'],
      },
      sourceaccountconfig: {
        config: accessJson.ess,
        data: ['source_account_config', 'isAccessible'],
      },
      riskconfig: {
        config: accessJson.ess,
        data: ['risk_config', 'isAccessible'],
      },
      bulkholdtransaction: {
        config: accessJson.ess,
        data: ['bulk_risk_config', 'isAccessible'],
      },
      updatepayoutcms: {
        config: accessJson.ess,
        data: ['payout_cms_update', 'isAccessible'],
      },
      updatebulkpayoutcms: {
        config: accessJson.ess,
        data: ['payout_cms_update', 'isAccessible'],
      },
      processingentity: {
        config: accessJson.ess,
        data: ['processing_entity', 'isAccessible'],
      },
      creditdebitadjustment: {
        config: accessJson.ess,
        data: ['crdr_adjustment', 'isAccessible'],
      },
      ['emsapprovals']: {
        config: accessJson.ess,
        data: getEssApprovalsAccess(accessJson.ess),
      },
      ['essbulkapprovals']: {
        config: accessJson.ess,
        data: ['crdr_adjustment', 'approve', 'isAccessible'],
      },
      ['essbulkapprovals']: {
        config: accessJson.ess,
        data: ['crdr_adjustment', 'approve', 'isAccessible'],
      },
      createBulkConfig: {
        config: accessJson.bulk_processing,
        data: ['create_configs', 'isAccessible'],
      },
      createBulkCards: {
        config: accessJson.bulk_processing,
        data: ['create_cards', 'isAccessible'],
      },
      //@HTAG:TODO PERMISSION
      createBulkMCCLov: {
        config: accessJson.bulk_processing,
        data: ['create_cards', 'isAccessible'],
      },
      createBulkPincodeLov: {
        config: accessJson.bulk_processing,
        data: ['create_cards', 'isAccessible'],
      },
      createBulkIfscLov: {
        config: accessJson.bulk_processing,
        data: ['create_cards', 'isAccessible'],
      },
      createBulkProgramCategoryLov: {
        config: accessJson.bulk_processing,
        data: ['create_cards', 'isAccessible'],
      },
      createBulkTerminalUpdate: {
        config: accessJson.bulk_processing,
        data: ['create_cards', 'isAccessible'],
      },
      createBulkStoreUpdate: {
        config: accessJson.bulk_processing,
        data: ['create_cards', 'isAccessible'],
      },
      createBulkDeviceUpdate: {
        config: accessJson.bulk_processing,
        data: ['create_cards', 'isAccessible'],
      },
      //HTAG TILL HERE

      fiuCompliance: {
        config: accessJson.fiu_compliance,
        data: ['modify', 'isAccessible'],
      },
      fiuQueue: {
        config: accessJson.fiu_compliance,
        data: ['checker', 'isAccessible'],
      },
      fiuMakerQueue: {
        config: accessJson.fiu_compliance,
        data: ['maker', 'isAccessible'],
      },
      aadharRedaction: {
        config: accessJson.aadhar_redaction,
        data: ['search', 'isAccessible'],
      },
      aadharCheckerQueue: {
        config: accessJson.aadhar_redaction,
        data: ['checker', 'isAccessible'],
      },
      aadharMakerQueue: {
        config: accessJson.aadhar_redaction,
        data: ['maker', 'isAccessible'],
      },
      createBulkVpa: {
        config: accessJson.bulk_processing,
        data: ['create_bulk_vpa', 'isAccessible'],
      },
      createBulkStore: {
        config: accessJson.bulk_processing,
        data: ['isAccessible'],
      },
      createBulkTerminal: {
        config: accessJson.bulk_processing,
        data: ['isAccessible'],
      },
      postRecon: {
        config: accessJson.post_recon,
        data: ['fetch', 'isAccessible'],
      },
      ['createBulkUsers']: {
        config: accessJson.merchant_config,
        data: ['merchant_mapping', 'add', 'isAccessible'],
      },
      ['pair-unpair-sqr']: {
        config: accessJson.merchant_config,
        data: ['device_mapping', 'isAccessible'],
      },
      ['fAndRAction']: {
        config: accessJson.ess,
        data: ['risk_config', 'isAccessible'],
      },
      ['fAndRAudit']: {
        config: accessJson.ess,
        data: ['risk_config', 'isAccessible'],
      },
      posBatchClosure: {
        config: accessJson.pos,
        data: ['batch_closure', 'create', 'isAccessible'],
      },
      posBatchClosureApprovals: {
        config: accessJson.pos,
        data: ['batch_closure', 'approve', 'isAccessible'],
      },
      bulkPosBatchClosure: {
        config: accessJson.pos,
        data: ['batch_closure', 'create', 'isAccessible'],
      },
      bulkPosBatchClosureApprovals: {
        config: accessJson.pos,
        data: ['batch_closure', 'approve', 'isAccessible'],
      },
      posapprovals: {
        config: accessJson.pos,
        data: ['epfa', 'fetch', 'isAccessible'],
      },
      checker1BulkClaim: {
        config: accessJson.entity_management_service,
        data: ['checkerOne', 'approve', 'isAccessible'],
      },
      checker1BulkAction: {
        config: accessJson.entity_management_service,
        data: ['checkerOne', 'approve', 'isAccessible'],
      },
      checker2BulkClaim: {
        config: accessJson.entity_management_service,
        data: ['checkerTwo', 'approve', 'isAccessible'],
      },
      checker2BulkAction: {
        config: accessJson.entity_management_service,
        data: ['checkerTwo', 'approve', 'isAccessible'],
      },
    }
    const access = getAccess[title]
    const isAccess = !access?.disabled && (access?.isApproval || get(access?.config, access?.data, false))
    if (title === 'bulkmerchantmapping') {
      return isAccess || get(access?.config, ['merchant_mapping', 'approve', 'isAccessible'])
    }

    if (title === 'emsapprovals') {
      return getEssApprovalsAccess(accessJson.ess)
    }
    if (title === 'approvals') {
      return getEntConfigApprovalsAccess(accessJson.entity_config)
    }
    if (title === 'routingapprovals') {
      return getRoutingApprovalAccess(accessJson.routing)
    }
    if (title === 'feepackageapprovals') {
      return getRoutingApprovalAccess(accessJson.mdr)
    }
    return isAccess
  } catch (error) {
    console.log('hasItemAccess', { error })
  }
}
